import React, { useEffect } from "react";
import {
	signInWithPopup,
	OAuthProvider,
	fetchSignInMethodsForEmail,
	linkWithCredential,
	EmailAuthProvider,
	signInWithEmailAndPassword,
	deleteUser,
	GoogleAuthProvider,
	reauthenticateWithPopup,
	signInWithCredential,
	signInWithCustomToken,
} from "firebase/auth";
import { auth } from "./firebaseConfig";
import { createUser } from "./modules/authentication/store/actions";
import { ExtendedGoogleLoginResponse } from "./modules/authentication/store/reducers";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { userServices } from "./modules/user/store/services";
import axios from "axios";
import { backendApi } from "./common/apiService";
const PARENT_FRAME = document.location.ancestorOrigins[0];

function sendResponse(result: any) {
			globalThis.parent.self.postMessage(JSON.stringify(result), PARENT_FRAME);
		}

const AuthComponent = () => {
	useEffect(() => {

		
		async function handleAuth(provider: any, providerType: string) {
			try {
				let result;

				// 🔹 GOOGLE
        alert(provider)
				if (provider === "offscreenGoogle") {
					const res = await SocialLogin.login({
						provider: "google",
						options: {},
					});

					const credential = GoogleAuthProvider.credential((res.result as ExtendedGoogleLoginResponse).idToken);
					result = await signInWithCredential(auth, credential);

					if (result && result.user) {
						const bearerToken = await result.user.getIdToken();

						const userCreated = await createUser(
							bearerToken,
							result.user.uid,
							result.user.displayName?.trim(),
							result.user.metadata.creationTime,
							result.user.email
						);

						if (!userCreated) {
							console.error("Failed to create user");
							return;
						}

						sendResponse(result);
					} else {
						console.error("SIGN IN - NO USER");
					}
				}

				// 🔹 MICROSOFT
				else if (provider === "offscreenMicrosoft") {
					result = await startMicrosoftAuth(); // uses your PKCE + custom token + Firebase sign-in flow internally
					return; // it handles everything, no further steps here
				}

				// 🔹 Other providers (optional future extension)
				else {
					throw new Error(`Unsupported provider: ${provider}`);
				}

				// 🔹 Common post-login logic (for Google or others that return a user)
			} catch (error: any) {
				if (error.code === "auth/account-exists-with-different-credential") {
					const email = error.customData?.email;
					let pendingCred;
					if (providerType === "google") {
						pendingCred = GoogleAuthProvider.credentialFromError(error);
					} else {
						pendingCred = provider.credentialFromError(error);
					}

					try {
						const methods = await fetchSignInMethodsForEmail(auth, email);
						if (methods.includes(EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD)) {
							const password = prompt("Please enter your password:");
							if (password && pendingCred) {
								const credential = EmailAuthProvider.credential(email, password);
								const userCredential = await signInWithEmailAndPassword(auth, email, password);
								await linkWithCredential(userCredential.user, pendingCred);
								sendResponse({ user: userCredential.user, linked: true });
							}
						} else {
							sendResponse({ error });
						}
					} catch (linkError) {
						sendResponse({ error: linkError });
					}
				} else {
					sendResponse({ error: error.message });
				}
			}
		}

		async function handleDeleteAccount(language: string) {
			try {
				const confirmed = await confirmAccountDeletion(language);
				if (!confirmed) {
					sendResponse({ message: "Account deletion cancelled by user." });
					return;
				}

				// Step 1: Social login (re-authenticate)
				const res = await SocialLogin.login({
					provider: "google",
					options: {},
				});

				// Step 2: Build Firebase credential
				const credential = GoogleAuthProvider.credential((res.result as ExtendedGoogleLoginResponse).idToken);

				// Step 3: Sign in with Firebase using the credential
				const result = await signInWithCredential(auth, credential);

				if (result && result.user) {
					// Step 4: Delete account from your backend
					await userServices.deleteAccount();

					sendResponse({ message: "Account deleted successfully." });
				} else {
					console.error("SIGN IN - NO USER");
					sendResponse({ error: "Failed to reauthenticate user." });
				}
			} catch (error: any) {
				console.error("DELETE ACCOUNT ERROR:", error);
				sendResponse({ error: error.message || error });
			}
		}

		async function confirmAccountDeletion(language: string) {
			// Implement your confirmation logic here.
			// For example, using a modal dialog in a web application:
			return new Promise((resolve) => {
				const confirmation = window.confirm(
					language === "en"
						? "Are you sure you want to delete your account? This action cannot be undone."
						: language === "it"
						? "Sei sicuro di voler eliminare il tuo account? Questa azione non può essere annullata."
						: language === "es"
						? "¿Estás seguro de que deseas eliminar tu cuenta? Esta acción no se puede deshacer."
						: language === "de"
						? "Sind Sie sicher, dass Sie Ihr Konto löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
						: "Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible."
				);
				resolve(confirmation);
			});
		}

		async function reauthenticateUser(providerId: string) {
			if (providerId === "google.com") {
				const provider = new GoogleAuthProvider();
				await signInWithPopup(auth, provider);
			} else if (providerId === "microsoft.com") {
				const provider = new OAuthProvider("microsoft.com");
				await signInWithPopup(auth, provider);
			} else if (providerId === "apple.com") {
				const provider = new OAuthProvider("apple.com");
				await signInWithPopup(auth, provider);
			} else {
				throw new Error("Unsupported authentication provider");
			}
		}

		globalThis.addEventListener("message", function ({ data }) {
			if (data.initAuthMicrosoft) {
				const provider = new OAuthProvider("microsoft.com");
				handleAuth("offscreenMicrosoft", "offscreenMicrosoft");
			} else if (data.initAuthApple) {
				const provider = new OAuthProvider("apple.com");
				handleAuth(provider, "apple.com");
			} else if (data.initAuthGoogle) {
				const provider = new GoogleAuthProvider();
				handleAuth("offscreenGoogle", "offscreenGoogle");
			} else if (data.deleteAccount) {
				handleDeleteAccount(data.language);
			}
		});
	}, []);

	return null;
};
export async function startMicrosoftAuth(del?: boolean) {
	const TENANT = "common";
	const CLIENT_ID = "4a11d142-7611-41d1-9fe7-ad586087f44a";
	const REDIRECT_URI_WEB = "https://app.reasyedu.com/authentication/get-started";
	const SCOPES = "openid profile email User.Read";

	localStorage.removeItem("pkce_verifier");
	const { verifier, challenge } = await generatePKCE();
	localStorage.setItem("pkce_verifier", verifier);

	const authUrl =
		`https://login.microsoftonline.com/${TENANT}/oauth2/v2.0/authorize?` +
		new URLSearchParams({
			client_id: CLIENT_ID,
			response_type: "code",
			redirect_uri: REDIRECT_URI_WEB,
			response_mode: "query",
			scope: SCOPES,
			code_challenge: challenge,
			code_challenge_method: "S256",
		}).toString();

	const parseJwt = (token: string) => {
		const payload = token.split(".")[1];
		const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		return JSON.parse(decoded);
	};

	const handleCode = async (codeParam: string | null) => {
		if (!codeParam) return;

		const savedVerifier = localStorage.getItem("pkce_verifier")!;
		try {
			const params = new URLSearchParams({
				client_id: CLIENT_ID,
				grant_type: "authorization_code",
				code: codeParam,
				redirect_uri: REDIRECT_URI_WEB,
				code_verifier: savedVerifier,
			});

			const tokenResponse = await axios.post(`https://login.microsoftonline.com/${TENANT}/oauth2/v2.0/token`, params, {
				headers: { "Content-Type": "application/x-www-form-urlencoded" },
			});

			const tokens = tokenResponse.data;
			const decoded = parseJwt(tokens.id_token);

			const customTokenResponse = await backendApi.post("/microsoftCustomToken", {
				id_token: tokens.id_token,
			});

			const firebaseToken = customTokenResponse.data.firebaseToken;
			if (!firebaseToken) throw new Error("No Firebase token returned");

			const res = await signInWithCustomToken(auth, firebaseToken);

			if (!res.user) return;

			const provider = new OAuthProvider("microsoft.com");
			const credentials = OAuthProvider.credentialFromResult(res);
			if (credentials) linkWithCredential(res.user, credentials);

			const bearerToken = await res.user.getIdToken();

			if (del) {
				await userServices.deleteAccount();
        sendResponse({ message: "Account deleted successfully." });

				return;
			}

			await createUser(bearerToken, res.user.uid, decoded.name, res.user.metadata.creationTime, decoded.email);
			return res;
		} catch (error: any) {
			if (axios.isAxiosError(error)) {
				console.error("Token exchange failed:", error.response?.data || error.message);
			} else {
				console.error("Unexpected error:", error);
			}
		}
	};

	const loginWindow = window.open(authUrl);
	if (!loginWindow) throw new Error("Popup blocked");

	const pollInterval = setInterval(() => {
		try {
			if (!loginWindow || loginWindow.closed) {
				clearInterval(pollInterval);
				return;
			}
			const url = loginWindow.location.href;
			if (url.includes("code=")) {
				const code = new URL(url).searchParams.get("code");
				loginWindow.close();
				clearInterval(pollInterval);
				handleCode(code);
			}
		} catch {
			// Ignore cross-origin access until redirect hits your domain
		}
	}, 500);
}
function base64URLEncode(str: ArrayBuffer) {
	return btoa(String.fromCharCode(...new Uint8Array(str)))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
}

async function generatePKCE() {
	const array = new Uint8Array(32);
	crypto.getRandomValues(array);
	const verifier = base64URLEncode(array.buffer);

	const encoder = new TextEncoder();
	const data = encoder.encode(verifier);
	const digest = await crypto.subtle.digest("SHA-256", data);
	const challenge = base64URLEncode(digest);

	return { verifier, challenge };
}
export default AuthComponent;
